import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Typography as MuiTypography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setEmail, setPhone, setLinks } from "../../../store/setProfile";

export default function ContactInfo({ onDirtyChange, onValidChange, onSave }: any) {
  const dispatch = useDispatch();
  const { email, phone, links } = useSelector(
    (state: any) => state.profile.information
  );
  const [mailError, setMailError] = useState(false);
  // Tutors explicitly opt in to showing WhatsApp on their profile. The
  // checkbox is auto-ticked if they already have a value stored — that way
  // returning to the step doesn't accidentally hide their button.
  const [whatsappOptIn, setWhatsappOptIn] = useState<boolean>(
    Boolean(links?.whatsapp)
  );
  useEffect(() => {
    if (links?.whatsapp && !whatsappOptIn) setWhatsappOptIn(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links?.whatsapp]);

  useEffect(() => {
    onDirtyChange?.(false);
  }, []);

  const validate = (name: string, value: string) => {
    let isValid = false;
    switch (name) {
      case "email":
        isValid = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i.test(value);
        break;
      case "phone":
        isValid = !value || /^(?:(?:\+|00)44|0)7(?:[1345789]\d{2}|624)\d{6}$/i.test(value);
        break;
      default:
        break;
    }
    return isValid;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const isValid = validate("email", value);
      setMailError(!isValid);
      dispatch(setEmail(value));
    } else {
      setMailError(false);
      dispatch(setEmail(value));
    }
    onDirtyChange?.(true);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setPhone(value));
    onDirtyChange?.(true);
  };

  useEffect(() => {
    // Optional step, always valid
    onValidChange?.(true);
  }, [onValidChange]);

  (ContactInfo as any).save = async () => {
    onDirtyChange?.(false);
  };

  return (
    <Box>
      <Typography 
        variant="h2"
        sx={{ 
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: '#712d94 !important' }}>
        How do clients get in touch with you?
        </Box>
      </Typography>
      <Box 
        sx={{ 
          '&': {
            marginBottom: '1.5rem !important'
          }
        }}
      >
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          (This section is optional)
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <MuiTypography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 4,
              color: "#333333",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            You'll receive client enquiries through our messaging system, with
            email alerts sent to your sign-up address. You can also choose to
            display your email or phone number on your profile – completely
            optional.
          </MuiTypography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel
              id="email-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              Your Email{" "}
              <MuiTypography
                component="span"
                sx={{ fontSize: "0.875rem", color: "grey", fontWeight: 400 }}
              >
                (Optional)
              </MuiTypography>
            </FormLabel>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter your email"
              value={email || ""}
              onChange={handleEmailChange}
              error={mailError}
              helperText={mailError ? "Please enter a valid email address" : ""}
            />
            <MuiTypography
              variant="caption"
              sx={{ mt: 0.5, color: "grey", fontSize: "0.875rem" }}
            >
              Your email will be shown in your profile
            </MuiTypography>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel
              id="phone-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              Your Telephone{" "}
              <MuiTypography
                component="span"
                sx={{ fontSize: "0.875rem", color: "grey", fontWeight: 400 }}
              >
                (Optional)
              </MuiTypography>
            </FormLabel>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter your phone"
              type="number"
              value={phone || ""}
              onChange={handlePhoneChange}
              onKeyDown={(e) => {
                if (e.key === "e" || e.key === ".") {
                  e.preventDefault();
                }
              }}
            />
            <MuiTypography
              variant="caption"
              sx={{ mt: 0.5, color: "grey", fontSize: "0.875rem" }}
            >
              Your telephone number will be shown on your profile
            </MuiTypography>
          </FormControl>

          {/* WhatsApp — explicit opt-in so the WhatsApp button only appears
              when the tutor wants it (we never derive it from the phone
              number automatically). */}
          <FormControl fullWidth sx={{ mt: 3 }}>
            <FormControlLabel
              sx={{
                m: 0,
                alignItems: "center",
                "& .MuiFormControlLabel-label": { ml: 0.5 },
              }}
              control={
                <Checkbox
                  sx={{ p: 0.5, "& .MuiSvgIcon-root": { marginBottom: "0.5rem" } }}
                  checked={whatsappOptIn}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setWhatsappOptIn(next);
                    if (!next) {
                      dispatch(setLinks({ whatsapp: "" }));
                      onDirtyChange?.(true);
                    }
                  }}
                />
              }
              label={
                <MuiTypography sx={{ fontWeight: 600, lineHeight: 1 }}>
                  Show a WhatsApp contact button on my profile
                </MuiTypography>
              }
            />
            {whatsappOptIn && (
              <>
                <TextField
                  sx={{ mt: 1 }}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g. +44 7700 900123 or wa.me/447700900123"
                  value={links?.whatsapp || ""}
                  onChange={(e) => {
                    dispatch(setLinks({ whatsapp: e.target.value }));
                    onDirtyChange?.(true);
                  }}
                />
                <MuiTypography
                  variant="caption"
                  sx={{ mt: 0.5, color: "grey", fontSize: "0.875rem" }}
                >
                  Enter the number you use for WhatsApp (we'll build a clickable
                  wa.me link). This is shown on your profile only while you're on
                  the Premium plan.
                </MuiTypography>
              </>
            )}
          </FormControl>

          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #e9e9ec",
            }}
          >
            <MuiTypography
              variant="body2"
              sx={{
                textAlign: "center",
                color: "#6B7280",
                fontSize: "0.9rem",
              }}
            >
              <strong>Next:</strong> You'll choose your plan – stay on the free Basic
              plan, or upgrade to Premium from £3.99/month (or £34.99/year, equivalent
              to £2.92/month). Cancel anytime.
            </MuiTypography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
